import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Product {
  product_id: string;
  name: string;
  description: string;
  category: string;
  price: string;
  shipping_required: boolean;
  inventory_count: number | null;
}

const ShopPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/package-builder/simple-products');
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data: Product[] = await response.json();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Array.from(new Set(products.map(p => p.category)))];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  const handleBuyNow = (productId: string) => {
    navigate(`/shop/checkout/${productId}`);
  };

  const getCategoryDisplayName = (category: string) => {
    switch (category) {
      case 'all': return 'All Products';
      case 'swag': return 'Swag & Apparel';
      case 'documentation': return 'Documentation';
      case 'hardware': return 'Hardware';
      case 'apparel': return 'Apparel';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading products...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          Error loading products: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          NickFixIt Shop
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Quality merchandise, documentation, and hardware for your self-hosting journey
        </p>
      </div>

      {/* Category Filter */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {getCategoryDisplayName(category)}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map(product => (
          <div key={product.product_id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
            {/* Product Image Placeholder */}
            <div className="h-48 bg-gray-100 rounded-t-lg flex items-center justify-center">
              <div className="text-gray-400 text-center">
                {product.category === 'swag' || product.category === 'apparel' ? '👕' :
                 product.category === 'documentation' ? '📚' :
                 product.category === 'hardware' ? '🔧' : '📦'}
                <div className="text-sm mt-2">{product.name}</div>
              </div>
            </div>

            <div className="p-4">
              {/* Category Badge */}
              <div className="mb-2">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {getCategoryDisplayName(product.category)}
                </span>
              </div>

              {/* Product Name */}
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                {product.name}
              </h3>

              {/* Description */}
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {product.description}
              </p>

              {/* Features */}
              <div className="mb-4 space-y-1">
                {product.shipping_required && (
                  <div className="flex items-center text-xs text-gray-500">
                    <span className="mr-1">🚚</span>
                    <span>Ships within 3-5 business days</span>
                  </div>
                )}
                {product.inventory_count && (
                  <div className="flex items-center text-xs text-gray-500">
                    <span className="mr-1">📦</span>
                    <span>{product.inventory_count} in stock</span>
                  </div>
                )}
              </div>

              {/* Price and Action */}
              <div className="flex items-center justify-between">
                <div className="text-xl font-bold text-gray-900">
                  ${product.price}
                </div>
                <button
                  onClick={() => handleBuyNow(product.product_id)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">
            No products found in this category
          </div>
          <button
            onClick={() => setSelectedCategory('all')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            View all products
          </button>
        </div>
      )}

      {/* Footer Note */}
      <div className="mt-16 text-center text-gray-500">
        <p className="text-sm">
          Looking for hosting services? Check out our{' '}
          <button
            onClick={() => navigate('/packages')}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            hosting packages
          </button>
          {' '}or{' '}
          <button
            onClick={() => navigate('/package-builder')}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            build a custom solution
          </button>
        </p>
      </div>
    </div>
  );
};

export default ShopPage;